import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as clientActions from "../../redux/actions/clientActions";
import NotificationService from "../../services/NotificationService";
import { displayDate, displayDateTime } from "../common/datePresentation";
import { formatLedgerAmount } from "./clientLedgerTypes";
import { buildManageRouteWithSchoolYear, getNormalizedSchoolYearStartYearFromSearch } from "./manageSchoolYearRouting";
import PaymentReminderPreviewModal, { reminderReason } from "./PaymentReminderPreviewModal";

export function PaymentRemindersPage({ service, clients, loadClients }) {
  const { t: tr, i18n } = useTranslation();
  const t = key => tr(`manage:paymentReminderV1.${key}`);
  const location = useLocation();
  const year = getNormalizedSchoolYearStartYearFromSearch(location.search);
  const [clientId, setClientId] = useState(new URLSearchParams(location.search).get("clientId") || "");
  const [status, setStatus] = useState("");
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  useEffect(() => {
    let cancelled = false;
    if (!clients.length) loadClients().catch(err => { if (!cancelled) setError(err.message); });
    return () => { cancelled = true; };
  }, [loadClients]);
  const generation = useRef(0);
  const loading = useRef(false);
  const load = async (nextCursor, revision) => {
    loading.current = true;
    setBusy(true);
    setError("");
    try {
      const result = await service.getPaymentReminderPreview({ schoolYearStartYear: year, clientId, status, cursor: nextCursor });
      if (generation.current !== revision) return;
      setItems(previous => (nextCursor ? [...previous, ...result.items] : result.items));
      setCursor(result.nextCursor);
    } catch (err) {
      if (generation.current === revision) setError(err.message || t("error"));
    } finally {
      if (generation.current === revision) { loading.current = false; setBusy(false); }
    }
  };
  useEffect(() => {
    generation.current += 1;
    setItems([]); setCursor(null); setSelected(null);
    load(null, generation.current);
    return () => { generation.current += 1; };
  }, [year, clientId, status, service]);
  const clientName = row => `${(row.client || {}).firstName || ""} ${(row.client || {}).lastName || ""}`.trim() || row.clientId;
  return (
    <div className="mb-5">
      <h2 className="manage-title">{t("title")}</h2>
      <p>{year}/{year + 1}</p>
      <div className="form-row">
        <div className="form-group col-md-6"><label htmlFor="reminder-client">{t("client")}</label>
          <select id="reminder-client" className="form-control" value={clientId} onChange={e => setClientId(e.target.value)}>
            <option value="">{t("all")}</option>
            {clientId && !clients.some(client => client.id === clientId) && <option value={clientId}>{clientId}</option>}
            {clients.map(client => <option key={client.id} value={client.id}>{clientName({ client, clientId: client.id })}</option>)}
          </select>
        </div>
        <div className="form-group col-md-6"><label htmlFor="reminder-status">{t("decision")}</label>
          <select id="reminder-status" className="form-control" value={status} onChange={e => setStatus(e.target.value)}>
            {["", "eligible", "deferred", "skipped"].map(value => <option key={value} value={value}>{t(value || "all")}</option>)}
          </select>
        </div>
      </div>
      {error && (
      <div className="alert alert-danger" role="alert">{error}
        <button type="button" className="btn manage-teal-button ml-2" disabled={busy} onClick={() => { generation.current += 1; load(null, generation.current); }}>{t("retry")}</button>
      </div>
      )}
      {busy && <p role="status">{t("loading")}</p>}
      {!busy && !error && !items.length && <p>{t("empty")}</p>}
      <div className="table-responsive">
        <table className="table table-sm manage-text">
          <thead><tr>{["client", "period", "amount", "due", "last", "limit", "decision", "next", "preview"].map(key => <th key={key} scope="col">{t(key)}</th>)}</tr></thead>
          <tbody>{items.map(row => (
            <tr key={row.obligationId || `${row.clientId}-${row.schoolYearStartYear}`}>
              <td>{clientName(row)}</td>
              <td>{row.schoolYearStartYear ? `${row.schoolYearStartYear}/${Number(row.schoolYearStartYear) + 1}` : "—"}<br />{row.settlementMonth}<br />
                {tr(`manage:paymentReminderV1.types.${row.notificationType}`, { defaultValue: row.notificationType || "—" })}
              </td>
              <td>{formatLedgerAmount(row.amountDue)}</td><td>{displayDate(row.dueDate) || "—"}</td>
              <td>{displayDateTime(row.lastAcceptedAt, i18n.language) || "—"}</td><td>{row.acceptedCount} / {row.maxAcceptedCount}</td>
              <td>{t(row.status)}{(row.reasonCodes || []).map(code => <div key={code}>{reminderReason(tr, code)}</div>)}</td>
              <td>{displayDateTime(row.nextEligibleAt, i18n.language) || "—"}</td>
              <td><button type="button" className="btn manage-teal-button" disabled={!row.obligationId} onClick={() => setSelected(row)}>{t("preview")}</button></td>
            </tr>
          ))}
          </tbody>
        </table>
      </div>
      {cursor && <button type="button" className="btn manage-teal-button mr-2" disabled={busy} onClick={() => { if (!loading.current) load(cursor, generation.current); }}>{t("more")}</button>}
      <Link className="btn manage-button manage-teal-button" to={buildManageRouteWithSchoolYear("/manage/reports/client-settlements", location.search, year)}>{tr("common:back")}</Link>
      {selected && <PaymentReminderPreviewModal row={selected} service={service} onHide={() => { setSelected(null); generation.current += 1; load(null, generation.current); }} />}
    </div>
  );
}
PaymentRemindersPage.propTypes = { service: PropTypes.object, clients: PropTypes.array, loadClients: PropTypes.func };
PaymentRemindersPage.defaultProps = { service: NotificationService, clients: [], loadClients: () => Promise.resolve() };
export default connect(state => ({ clients: state.clients }), { loadClients: clientActions.loadClients })(PaymentRemindersPage);
